import React from 'react'

import {
  IndentDecreaseIcon,
  IndentIncreaseIcon,
  ListPlusIcon,
  PlusIcon,
  SearchIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import { CollapseButton } from '~/client/src/shared/components/CollapseButton/CollapseButton'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import { Input } from '~/client/src/shared/components/shadcn-ui/input'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import PropertyAccordion from '../ui/PropertyAccordion/PropertyAccordion'
import BulkAddHierarchyOptionsDialog from './BulkAddHierarchyOptionsDialog'
import { gridTemplateColumns } from './HierarchyOptionRow'
import HierarchyOptionsList from './HierarchyOptionsList'
import type { HierarchyConfiguration } from './HierarchyPropertyConfig.types'
import { useHierarchyOptionsEditor } from './useHierarchyOptionsEditor'

interface IHierarchyPropertyConfigProps {
  configuration: HierarchyConfiguration
  isCreateMode?: boolean
  usedOptionValues?: Set<string>
  isUsageUnknown?: boolean
  hasUsageError?: boolean
  // The location type list: its kinds are fixed, so nothing may be added or
  // moved to another level. Labels, sibling order and removal stay editable.
  isFixedVocabulary?: boolean
  onConfigChange: (
    configUpdater: (prev: HierarchyConfiguration) => HierarchyConfiguration,
  ) => void
}

const HierarchyPropertyConfig: React.FC<IHierarchyPropertyConfigProps> = ({
  configuration,
  isCreateMode = false,
  usedOptionValues,
  isUsageUnknown = false,
  hasUsageError = false,
  isFixedVocabulary = false,
  onConfigChange,
}) => {
  const {
    optionValues,
    rows,
    parentValues,
    expandedValues,
    areAllExpanded,
    searchMatchedValues,
    selectedSearchMatchCount,
    activeSelectedValues,
    selectedCount,
    searchTerm,
    setSearchTerm,
    bulkText,
    setBulkText,
    isBulkDialogOpen,
    setBulkDialogOpen,
    restoredOptionLabels,
    dismissRestoredOptions,
    pendingFocusValue,
    onAutoFocusHandled,
    getNameDraftValidation,
    addOption,
    insertOptionBelow,
    updateOption,
    markNameManuallyEdited,
    handleLabelBlur,
    restoreAndApply,
    setSelected,
    setSelectAll,
    toggleExpanded,
    toggleAllExpanded,
    indentSelected,
    outdentSelected,
    deleteSelected,
    reorder,
    bulkAdd,
  } = useHierarchyOptionsEditor({
    configuration,
    isCreateMode,
    usedOptionValues,
    isUsageUnknown,
    hasUsageError,
    isFixedVocabulary,
    onConfigChange,
  })

  return (
    <PropertyAccordion
      title={Localization.translator.dropdownOptions}
      isDefaultExpanded
    >
      <div className="tw:flex tw:flex-col">
        <p className="tw:m-0 tw:px-4 tw:pt-4 tw:text-body-sm tw:text-muted-foreground">
          {Localization.translator.hierarchyOptionsIntro}
        </p>

        {restoredOptionLabels.length > 0 && (
          <div className="tw:mx-4 tw:mt-4 tw:flex tw:items-start tw:gap-2 tw:rounded-md tw:bg-accent tw:px-3 tw:py-2 tw:text-body-sm tw:text-accent-foreground">
            <span className="tw:flex-1">
              {Localization.translator.restoredDeletedOptions(
                restoredOptionLabels.join(', '),
                restoredOptionLabels.length,
              )}
            </span>
            <button
              type="button"
              aria-label={Localization.translator.dismiss}
              className="tw:flex tw:size-5 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-sm tw:border-0 tw:bg-transparent tw:cursor-pointer tw:text-accent-foreground"
              onClick={dismissRestoredOptions}
            >
              <XIcon className="tw:size-4" />
            </button>
          </div>
        )}

        <div className="tw:sticky tw:top-0 tw:z-20 tw:bg-background">
          <div className="tw:px-4 tw:pb-3 tw:pt-4">
            <div className="tw:relative">
              <SearchIcon className="tw:pointer-events-none tw:absolute tw:left-2.5 tw:top-1/2 tw:size-4 tw:-translate-y-1/2 tw:text-muted-foreground" />
              <Input
                value={searchTerm}
                placeholder={Localization.translator.search}
                className="tw:pl-8"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(event.target.value)
                }
              />
            </div>
          </div>
          <div className="tw:px-4">
            <div
              className="tw:grid tw:items-center tw:border-0 tw:border-b tw:border-solid tw:border-border tw:px-2 tw:py-2 tw:text-body-sm tw:font-medium tw:text-muted-foreground"
              style={{ gridTemplateColumns }}
            >
              <div />
              <div className="tw:flex tw:items-center tw:justify-center">
                <Checkbox
                  checked={
                    searchMatchedValues.length > 0 &&
                    selectedSearchMatchCount === searchMatchedValues.length
                  }
                  indeterminate={
                    selectedSearchMatchCount > 0 &&
                    selectedSearchMatchCount < searchMatchedValues.length
                  }
                  aria-label={Localization.translator.selectAllOptions}
                  onCheckedChange={checked => setSelectAll(checked === true)}
                />
              </div>
              <div className="tw:flex tw:items-center tw:justify-between tw:gap-1">
                <div className="tw:flex tw:items-center tw:gap-1">
                  {!searchTerm.trim() && parentValues.size > 0 && (
                    <CollapseButton
                      isExpanded={areAllExpanded}
                      onClick={toggleAllExpanded}
                      size="icon-xs"
                      ariaLabel={
                        areAllExpanded
                          ? Localization.translator.collapseAll
                          : Localization.translator.expandAll
                      }
                    />
                  )}
                  <span
                    className={
                      !searchTerm.trim() && parentValues.size > 0
                        ? undefined
                        : 'tw:pl-7'
                    }
                  >
                    {Localization.translator.optionLabel}
                  </span>
                </div>
                <span className="tw:font-normal">
                  {searchTerm.trim()
                    ? Localization.translator.optionMatchCount(
                        searchMatchedValues.length,
                        optionValues.size,
                      )
                    : Localization.translator.optionCount(optionValues.size)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <HierarchyOptionsList
          rows={rows}
          selectedValues={activeSelectedValues}
          expandedValues={expandedValues}
          onSelectedChange={setSelected}
          onExpandedToggle={toggleExpanded}
          onUpdateOption={updateOption}
          onNameManuallyEdited={markNameManuallyEdited}
          onLabelBlur={handleLabelBlur}
          getNameDraftValidation={getNameDraftValidation}
          onRestoreAndApply={restoreAndApply}
          onReorder={reorder}
          // Positional insert is hidden while searching (like the collapse-all
          // toggle): the filtered view drops non-matching rows, so a new option
          // would be created invisibly.
          onInsertBelow={
            searchTerm.trim() || isFixedVocabulary
              ? undefined
              : insertOptionBelow
          }
          autoFocusValue={pendingFocusValue}
          onAutoFocusHandled={onAutoFocusHandled}
        />

        <div className="tw:sticky tw:bottom-0 tw:z-20 tw:mt-2 tw:border-0 tw:border-t tw:border-solid tw:border-border tw:bg-background tw:px-4 tw:py-3">
          {selectedCount > 0 ? (
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
              <span className="tw:mr-2 tw:text-body-md tw:text-muted-foreground">
                {Localization.translator.optionsSelected(selectedCount)}
              </span>
              {!isFixedVocabulary && (
                <>
                  <Button variant="outline" onClick={indentSelected}>
                    <IndentIncreaseIcon data-icon="inline-start" />
                    {Localization.translator.indent}
                  </Button>
                  <Button variant="outline" onClick={outdentSelected}>
                    <IndentDecreaseIcon data-icon="inline-start" />
                    {Localization.translator.outdent}
                  </Button>
                </>
              )}
              <Button variant="destructive" onClick={deleteSelected}>
                <Trash2Icon data-icon="inline-start" />
                {Localization.translator.delete}
              </Button>
            </div>
          ) : isFixedVocabulary ? null : (
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
              <Button
                variant="outline"
                onClick={addOption}
                disabled={!!searchTerm.trim()}
              >
                <PlusIcon data-icon="inline-start" />
                {Localization.translator.addOption}
              </Button>
              <Button
                variant="outline"
                onClick={() => setBulkDialogOpen(true)}
                disabled={!!searchTerm.trim()}
              >
                <ListPlusIcon data-icon="inline-start" />
                {Localization.translator.bulkAddOptions}
              </Button>
            </div>
          )}
        </div>
      </div>

      <BulkAddHierarchyOptionsDialog
        isOpen={isBulkDialogOpen}
        value={bulkText}
        onOpenChange={setBulkDialogOpen}
        onValueChange={setBulkText}
        onAdd={bulkAdd}
      />
    </PropertyAccordion>
  )
}

export default HierarchyPropertyConfig
